import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { adminApi, type AgentRun } from '../../api/adminApi';
import { Badge, DataTable, type ColumnDef, type SortState } from '@shared-ui/components';
import { deviceLabel } from './deviceLabel';

const STATUS_VARIANT: Record<string, 'info' | 'warning' | 'success' | 'error' | 'default'> = {
  running: 'warning', completed: 'success', failed: 'error', partial: 'info',
};

function avgRatingNum(r: AgentRun): number | null {
  const rs = (r.perPersona ?? []).map(p => p.rating).filter((x): x is number => typeof x === 'number');
  return rs.length ? rs.reduce((a, b) => a + b, 0) / rs.length : null;
}
function avgRating(r: AgentRun): string {
  const v = avgRatingNum(r);
  return v === null ? '—' : `${v.toFixed(1)}/5`;
}
function findingsCount(r: AgentRun): number {
  return (r.perPersona ?? []).reduce((n, p) => n + (p.feedbackCount ?? 0), 0);
}

function durationStr(r: AgentRun): string | null {
  if (!r.startedAt || !r.endedAt) return null;
  const s = Math.round((new Date(r.endedAt).getTime() - new Date(r.startedAt).getTime()) / 1000);
  if (s < 0) return null;
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

// Exact cost when the run recorded it; else an input-rate estimate (output is ~1-2%).
function costStr(r: AgentRun): string | null {
  const usd = r.costUsd ?? (r.tokensSpent ? (r.tokensSpent / 1e6) * 0.30 : undefined);
  return usd === undefined ? null : `${r.costUsd === undefined ? '~' : ''}$${usd.toFixed(3)}`;
}

// Distinct device labels for a run's roster (a run may span several personas, e.g. a
// cohort). Device lives on the persona spec, so it's looked up via deviceByPersona.
function runDeviceLabels(r: AgentRun, deviceByPersona: Map<string, string>): string[] {
  const labels = (r.roster ?? []).map(id => deviceByPersona.get(id)).filter((x): x is string => !!x);
  return [...new Set(labels)];
}

export function RunsTab() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState<SortState>({ sortBy: 'started', sortOrder: 'desc' });
  const { data, isLoading } = useQuery({
    queryKey: ['agent-runs', search, status],
    queryFn: () => adminApi.getAgentRuns({ limit: 200, search: search || undefined, status: status || undefined }),
  });
  // Personas → device map (shared cache with the Personas tab; runs carry no device).
  const { data: personaData } = useQuery({ queryKey: ['agent-personas'], queryFn: () => adminApi.getAgentPersonas() });
  const deviceByPersona = useMemo(
    () => new Map((personaData?.personas ?? []).map(p => [p.personaId, deviceLabel(p.spec?.device)])),
    [personaData],
  );
  const runs = data?.runs ?? [];

  // Sort keys → a comparable value per run. Nulls (no value) always sink to the bottom.
  const sortAccessors = useMemo<Record<string, (r: AgentRun) => number | string | null>>(() => ({
    started: r => (r.startedAt ? new Date(r.startedAt).getTime() : null),
    personas: r => (r.roster ?? []).join(', ').toLowerCase(),
    device: r => runDeviceLabels(r, deviceByPersona).join(', '),
    status: r => r.status ?? '',
    rating: r => avgRatingNum(r),
    findings: r => findingsCount(r),
    tokens: r => r.tokensSpent ?? null,
  }), [deviceByPersona]);

  const sortedRuns = useMemo(() => {
    const acc = sortAccessors[sort.sortBy];
    if (!acc) return runs;
    const dir = sort.sortOrder === 'asc' ? 1 : -1;
    return [...runs].sort((a, b) => {
      const va = acc(a), vb = acc(b);
      // Nulls (no value) always sink to the bottom, regardless of direction.
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return 0;
    });
  }, [runs, sort, sortAccessors]);

  const columns = useMemo<ColumnDef<AgentRun>[]>(() => [
    {
      key: 'started', header: 'Started', sortable: true, render: r => {
        if (!r.startedAt) return '—';
        const dur = durationStr(r);
        return <span>{format(new Date(r.startedAt), 'MMM d HH:mm')}{dur ? <span className="pa-muted"> ({dur})</span> : ''}</span>;
      },
    },
    { key: 'personas', header: 'Personas', sortable: true, render: r => (r.roster ?? []).join(', ') },
    {
      key: 'device', header: 'Device', sortable: true, render: r => {
        const labels = runDeviceLabels(r, deviceByPersona);
        return labels.length
          ? <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>{labels.map(l => <Badge key={l} variant="default" size="sm">{l}</Badge>)}</span>
          : '—';
      },
    },
    { key: 'status', header: 'Status', sortable: true, render: r => <Badge variant={STATUS_VARIANT[r.status] ?? 'default'} size="sm">{r.status}</Badge> },
    { key: 'rating', header: 'Rating', sortable: true, className: 'pa-num', render: r => avgRating(r) },
    { key: 'findings', header: 'Findings', sortable: true, className: 'pa-num', render: r => findingsCount(r) || '—' },
    {
      key: 'tokens', header: 'Tokens', sortable: true, className: 'pa-num', render: r => {
        if (!r.tokensSpent) return '—';
        const cost = costStr(r);
        return <span>{r.tokensSpent.toLocaleString()}{cost ? <div className="pa-muted" style={{ fontSize: '0.8em' }}>{cost}</div> : ''}</span>;
      },
    },
  ], [deviceByPersona]);

  return (
    <>
      <div className="pa-filters">
        <input className="pa-input" placeholder="Search persona…" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="pa-input" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="completed">completed</option>
          <option value="failed">failed</option>
          <option value="running">running</option>
          <option value="partial">partial</option>
        </select>
      </div>
      <DataTable<AgentRun>
        columns={columns}
        data={sortedRuns}
        rowKey={r => r.id}
        sort={sort}
        onSortChange={setSort}
        onRowClick={r => navigate(`/persona-agents/runs/${encodeURIComponent(r.runId)}`)}
        loading={isLoading}
        emptyMessage="No agent runs"
      />
    </>
  );
}
